import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { FileUpload } from '@/components/ui/file-upload'
import { Toast } from '@/components/ui/toast'
import {
  AlertCircle,
  Bike,
  Clock,
  Upload,
  MapPin,
  Shield,
  ChevronDown,
  Star,
  DollarSign,
  Calendar,
  Car,
  Truck,
  PersonStanding
} from 'lucide-react'
import { supabase } from '@/lib/supabase'

interface DeliveryForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  vehicleType: 'bike' | 'motorcycle' | 'car' | 'van' | 'none'
  deliveryZone: string
  availability: {
    monday: boolean
    tuesday: boolean
    wednesday: boolean
    thursday: boolean
    friday: boolean
    saturday: boolean
    sunday: boolean
  }
  idFrontDocument: File | null
  idBackDocument: File | null
  proofOfAddress: File | null
  selfieWithId: File | null
  vehicleRegistration: File | null
  vehicleInsurance: File | null
}

export function DeliveryRegisterPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [existingApplication, setExistingApplication] = useState(false)
  const [loading, setLoading] = useState(true)
  const [currentSection, setCurrentSection] = useState(0)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [form, setForm] = useState<DeliveryForm>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    vehicleType: 'none',
    deliveryZone: '',
    availability: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    },
    idFrontDocument: null,
    idBackDocument: null,
    proofOfAddress: null,
    selfieWithId: null,
    vehicleRegistration: null,
    vehicleInsurance: null,
  })
  const [error, setError] = useState('')
  const [showFaq, setShowFaq] = useState<number | null>(null)
  
  // Add file input refs for mobile
  const idFrontRef = useRef<HTMLInputElement>(null)
  const idBackRef = useRef<HTMLInputElement>(null)
  const proofOfAddressRef = useRef<HTMLInputElement>(null)
  const selfieWithIdRef = useRef<HTMLInputElement>(null)
  const vehicleRegistrationRef = useRef<HTMLInputElement>(null)
  const vehicleInsuranceRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    checkExistingApplication()
  }, [])

  const checkExistingApplication = async () => {
    if (!user) {
      navigate('/login')
      return
    }

    try {
      const { data, error } = await supabase
        .from('delivery_partners')
        .select('id, status')
        .eq('id', user.id)
        .maybeSingle()

      if (error && error.code !== 'PGRST116') {
        throw error
      }

      if (data) {
        setExistingApplication(true)
        navigate('/delivery/application-status')
      }
    } catch (err) {
      console.error('Error checking application:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (existingApplication) {
    return null
  }

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof Pick<DeliveryForm, 'idFrontDocument' | 'idBackDocument' | 'proofOfAddress' | 'selfieWithId' | 'vehicleRegistration' | 'vehicleInsurance'>
  ) => {
    console.log(`handleFileChange called for ${field}`, e.target.files);
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(`Le fichier est trop volumineux. La taille maximale est de 5MB.`);
        return;
      }
      
      setForm((prev) => ({ ...prev, [field]: e.target.files![0] }));
      console.log(`File set for ${field}:`, file.name, file.size);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) {
      setError('You must be logged in to submit an application')
      return
    }

    setError('')
    setLoading(true)

    try {
      const { data: existingData } = await supabase
        .from('delivery_partners')
        .select('id')
        .eq('id', user.id)
        .maybeSingle()

      if (existingData) {
        setError('You have already submitted a delivery partner application')
        return
      }

      const documentUrls: Record<string, string | null> = {
        id_front: null,
        id_back: null,
        proof_of_address: null,
        selfie_with_id: null,
        vehicle_registration: null,
        vehicle_insurance: null
      }

      const uploadDocument = async (file: File | null, key: string) => {
        if (!file || !user) return null

        const fileExt = file.name.split('.').pop()
        const fileName = `${user.id}/${key}.${fileExt}`

        console.log(`Uploading ${key} document:`, fileName);
        
        try {
          const { error: uploadError } = await supabase.storage
            .from('delivery-documents')
            .upload(fileName, file, { upsert: true })

          if (uploadError) {
            console.error(`Error uploading ${key}:`, uploadError);
            throw uploadError;
          }

          const { data: { publicUrl } } = supabase.storage
            .from('delivery-documents')
            .getPublicUrl(fileName)

          console.log(`${key} uploaded successfully, URL:`, publicUrl);
          return publicUrl;
        } catch (err) {
          console.error(`Error in uploadDocument for ${key}:`, err);
          throw err;
        }
      }

      await Promise.all([
        (async () => {
          documentUrls.id_front = await uploadDocument(form.idFrontDocument, 'id-front')
        })(),
        (async () => {
          documentUrls.id_back = await uploadDocument(form.idBackDocument, 'id-back')
        })(),
        (async () => {
          documentUrls.proof_of_address = await uploadDocument(form.proofOfAddress, 'proof-of-address')
        })(),
        (async () => {
          documentUrls.selfie_with_id = await uploadDocument(form.selfieWithId, 'selfie-with-id')
        })(),
        form.vehicleType !== 'none' && (async () => {
          documentUrls.vehicle_registration = await uploadDocument(form.vehicleRegistration, 'vehicle-registration')
        })(),
        form.vehicleType !== 'none' && (async () => {
          documentUrls.vehicle_insurance = await uploadDocument(form.vehicleInsurance, 'vehicle-insurance')
        })()
      ])

      console.log('All documents uploaded, document URLs:', documentUrls);

      const { error: partnerError } = await supabase
        .from('delivery_partners')
        .insert([
          {
            id: user.id,
            vehicle_type: form.vehicleType,
            delivery_zone: form.deliveryZone,
            verification_status: 'pending_review',
            status: 'pending',
            availability: form.availability,
            document_urls: documentUrls,
            applicant_name: `${form.firstName} ${form.lastName}`,
            applicant_email: form.email,
            applicant_phone: form.phone
          }
        ])

      if (partnerError) {
        console.error('Error inserting delivery partner:', partnerError);
        throw partnerError;
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          role: 'delivery',
          full_name: `${form.firstName} ${form.lastName}`,
          phone_number: form.phone,
          email: form.email
        })
        .eq('id', user.id)

      if (profileError) {
        console.error('Error updating profile:', profileError);
        throw profileError;
      }

      setToastMessage('Application submitted successfully!');
      setToastType('success');
      setShowToast(true);
      
      setTimeout(() => {
        navigate('/')
      }, 2000)
    } catch (err) {
      console.error('Error submitting application:', err);
      setError('Failed to submit application. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const sections = [
    {
      title: 'Personal Information',
      description: 'Tell us about yourself',
      content: (
        <div className="space-y-8">
          <div className="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                required
                value={form.firstName}
                onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                required
                value={form.lastName}
                onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                required
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                required
                value={form.phone}
                onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Delivery Information',
      description: 'Set up your delivery preferences',
      content: (
        <div className="space-y-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Vehicle Type
            </label>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {[
                { value: 'bike', label: 'Bicycle', icon: Bike },
                { value: 'motorcycle', label: 'Motorcycle', icon: Bike },
                { value: 'car', label: 'Car', icon: Car },
                { value: 'van', label: 'Van', icon: Truck },
                { value: 'none', label: 'No Vehicle', icon: PersonStanding },
              ].map((vehicle) => (
                <button
                  key={vehicle.value}
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, vehicleType: vehicle.value as any }))}
                  className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                    form.vehicleType === vehicle.value
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <vehicle.icon className={`h-8 w-8 ${
                    form.vehicleType === vehicle.value ? 'text-primary' : 'text-gray-400'
                  }`} />
                  <span className="mt-2 text-sm font-medium text-gray-900">{vehicle.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="deliveryZone" className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Delivery Zone
            </label>
            <input
              type="text"
              id="deliveryZone"
              required
              value={form.deliveryZone}
              onChange={(e) => setForm((prev) => ({ ...prev, deliveryZone: e.target.value }))}
              className="block w-full h-12 px-4 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter your preferred delivery area"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Availability
            </label>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {Object.entries(form.availability).map(([day, checked]) => (
                <label
                  key={day}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    checked ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        availability: {
                          ...prev.availability,
                          [day]: e.target.checked,
                        },
                      }))
                    }
                    className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900 capitalize">
                    {day}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Verification',
      description: 'Verify your identity',
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="idFrontDocument" className="block text-sm font-medium text-gray-700 mb-2">
                ID Card (Front) *
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                <input
                  ref={idFrontRef}
                  type="file"
                  id="idFrontDocument"
                  name="idFrontDocument"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, 'idFrontDocument')}
                  required
                />
                <button
                  type="button"
                  onClick={() => idFrontRef.current?.click()}
                  className="w-full h-full flex flex-col items-center justify-center p-6"
                >
                  {form.idFrontDocument ? (
                    <>
                      <div className="text-primary bg-primary/10 rounded-full p-2">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                        {form.idFrontDocument.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(form.idFrontDocument.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">
                        Cliquez pour ajouter
                      </p>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="idBackDocument" className="block text-sm font-medium text-gray-700 mb-2">
                ID Card (Back) *
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                <input
                  ref={idBackRef}
                  type="file"
                  id="idBackDocument"
                  name="idBackDocument"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, 'idBackDocument')}
                  required
                />
                <button
                  type="button"
                  onClick={() => idBackRef.current?.click()}
                  className="w-full h-full flex flex-col items-center justify-center p-6"
                >
                  {form.idBackDocument ? (
                    <>
                      <div className="text-primary bg-primary/10 rounded-full p-2">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                        {form.idBackDocument.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(form.idBackDocument.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">
                        Cliquez pour ajouter
                      </p>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="proofOfAddress" className="block text-sm font-medium text-gray-700 mb-2">
                Proof of Address *
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                <input
                  ref={proofOfAddressRef}
                  type="file"
                  id="proofOfAddress"
                  name="proofOfAddress"
                  accept="image/*,.pdf"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, 'proofOfAddress')}
                  required
                />
                <button
                  type="button"
                  onClick={() => proofOfAddressRef.current?.click()}
                  className="w-full h-full flex flex-col items-center justify-center p-6"
                >
                  {form.proofOfAddress ? (
                    <>
                      <div className="text-primary bg-primary/10 rounded-full p-2">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                        {form.proofOfAddress.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(form.proofOfAddress.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">
                        Cliquez pour ajouter
                      </p>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="selfieWithId" className="block text-sm font-medium text-gray-700 mb-2">
                Selfie with ID *
              </label>
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                <input
                  ref={selfieWithIdRef}
                  type="file"
                  id="selfieWithId"
                  name="selfieWithId"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => handleFileChange(e, 'selfieWithId')}
                  required
                />
                <button
                  type="button"
                  onClick={() => selfieWithIdRef.current?.click()}
                  className="w-full h-full flex flex-col items-center justify-center p-6"
                >
                  {form.selfieWithId ? (
                    <>
                      <div className="text-primary bg-primary/10 rounded-full p-2">
                        <Upload className="h-5 w-5" />
                      </div>
                      <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                        {form.selfieWithId.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(form.selfieWithId.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-gray-400" />
                      <p className="mt-1 text-sm text-gray-500">
                        Cliquez pour ajouter
                      </p>
                    </>
                  )}
                </button>
              </div>
            </div>

            {form.vehicleType !== 'none' && (
              <>
                <div>
                  <label htmlFor="vehicleRegistration" className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle Registration *
                  </label>
                  <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                    <input
                      ref={vehicleRegistrationRef}
                      type="file"
                      id="vehicleRegistration"
                      name="vehicleRegistration"
                      accept="image/*,.pdf"
                      className="sr-only"
                      onChange={(e) => handleFileChange(e, 'vehicleRegistration')}
                      required={form.vehicleType !== 'none'}
                    />
                    <button
                      type="button"
                      onClick={() => vehicleRegistrationRef.current?.click()}
                      className="w-full h-full flex flex-col items-center justify-center p-6"
                    >
                      {form.vehicleRegistration ? (
                        <>
                          <div className="text-primary bg-primary/10 rounded-full p-2">
                            <Upload className="h-5 w-5" />
                          </div>
                          <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                            {form.vehicleRegistration.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(form.vehicleRegistration.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-gray-400" />
                          <p className="mt-1 text-sm text-gray-500">
                            Cliquez pour ajouter
                          </p>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="vehicleInsurance" className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle Insurance *
                  </label>
                  <div className="relative border-2 border-dashed border-gray-300 rounded-lg hover:border-primary transition-colors">
                    <input
                      ref={vehicleInsuranceRef}
                      type="file"
                      id="vehicleInsurance"
                      name="vehicleInsurance"
                      accept="image/*,.pdf"
                      className="sr-only"
                      onChange={(e) => handleFileChange(e, 'vehicleInsurance')}
                      required={form.vehicleType !== 'none'}
                    />
                    <button
                      type="button"
                      onClick={() => vehicleInsuranceRef.current?.click()}
                      className="w-full h-full flex flex-col items-center justify-center p-6"
                    >
                      {form.vehicleInsurance ? (
                        <>
                          <div className="text-primary bg-primary/10 rounded-full p-2">
                            <Upload className="h-5 w-5" />
                          </div>
                          <p className="mt-2 text-sm text-primary font-medium truncate max-w-full">
                            {form.vehicleInsurance.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(form.vehicleInsurance.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-gray-400" />
                          <p className="mt-1 text-sm text-gray-500">
                            Cliquez pour ajouter
                          </p>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800">
              Important Instructions:
            </h4>
            <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
              <li>Upload clear, high-quality images of both sides of your ID card</li>
              <li>Ensure all text on the ID is clearly visible</li>
              <li>Your selfie should show you holding your ID next to your face</li>
              <li>Proof of address must be less than 3 months old</li>
              {form.vehicleType !== 'none' && (
                <>
                  <li>Vehicle registration must be current and in your name</li>
                  <li>Insurance documents must show valid coverage dates</li>
                </>
              )}
            </ul>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {showToast && (
        <Toast
          type="success"
          title="Application Submitted Successfully!"
          message="You will be redirected to the home page."
          onClose={() => setShowToast(false)}
          position="center"
        />
      )}

      <div className="relative bg-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div>
              <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
                Travaillez à votre rythme avec AEM Marketplace !
              </h1>
              <p className="mt-4 text-lg">
                Gagnez de l'argent en effectuant des livraisons près de chez vous.
                Choisissez vos horaires et vos missions en toute liberté.
              </p>
            </div>
            <div className="mt-12 lg:mt-0">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <Clock className="h-8 w-8" />
                  <h3 className="mt-4 text-lg font-medium">Flexibilité Totale</h3>
                  <p className="mt-2 text-sm text-gray-100">
                    Choisissez vos horaires et missions
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <DollarSign className="h-8 w-8" />
                  <h3 className="mt-4 text-lg font-medium">Gains intéressants</h3>
                  <p className="mt-2 text-sm text-gray-100">
                    Augmentez vos revenus quand vous le souhaitez
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <MapPin className="h-8 w-8" />
                  <h3 className="mt-4 text-lg font-medium">Livraison locale</h3>
                  <p className="mt-2 text-sm text-gray-100">
                    Sélectionnez des livraisons près de chez vous
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                  <Calendar className="h-8 w-8" />
                  <h3 className="mt-4 text-lg font-medium">Planning flexible</h3>
                  <p className="mt-2 text-sm text-gray-100">
                    Adaptez vos horaires selon vos besoins
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="px-4 sm:px-6 lg:px-8">
              <nav className="-mb-px flex space-x-8">
                {sections.map((section, index) => (
                  <button
                    key={section.title}
                    onClick={() => setCurrentSection(index)}
                    className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                      currentSection === index
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="bg-gray-100 rounded-full w-6 h-6 inline-flex items-center justify-center mr-2">
                      {index + 1}
                    </span>
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-4 py-8 sm:px-6 lg:px-8">
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {sections[currentSection].title}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {sections[currentSection].description}
                </p>
              </div>

              {sections[currentSection].content}

              <div className="mt-8 flex justify-between">
                {currentSection > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setCurrentSection(currentSection - 1)}
                  >
                    Previous
                  </Button>
                )}
                {currentSection < sections.length - 1 ? (
                  <Button
                    type="button"
                    variant="primary"
                    className="ml-auto"
                    onClick={() => setCurrentSection(currentSection + 1)}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="primary"
                    className="ml-auto"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Create Delivery Partner Account'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900">Questions Fréquentes</h2>
          <dl className="mt-6 space-y-4">
            {[
              {
                question: "Combien puis-je gagner en tant que livreur ?",
                answer:
                  "Les revenus dépendent du nombre de livraisons effectuées et des distances parcourues. En moyenne, nos livreurs gagnent entre 15$ et 25$ de l'heure.",
              },
              {
                question: "Puis-je refuser une mission ?",
                answer:
                  "Oui, vous êtes libre d'accepter ou de refuser les missions. Cependant, un taux élevé de refus peut affecter votre classement.",
              },
              {
                question: "Quelles zones puis-je livrer ?",
                answer:
                  "Vous choisissez votre zone préférée lors de l'inscription. Vous pouvez modifier cette zone à tout moment depuis votre tableau de bord.",
              },
              {
                question: "Comment suis-je payé ?",
                answer:
                  "Les paiements sont effectués directement sur votre compte bancaire chaque semaine. Vous pouvez suivre vos gains en temps réel dans votre tableau de bord.",
              }
            ].map((faq, index) => (
              <div key={index} className="border-b border-gray-200 pb-4">
                <button
                  onClick={() => setShowFaq(showFaq === index ? null : index)}
                  className="w-full text-left"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                    <ChevronDown
                      className={`h-5 w-5 text-gray-500 transition-transform ${
                        showFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                {showFaq === index && (
                  <p className="mt-4 text-gray-600">{faq.answer}</p>
                )}
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  )
}